import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getAftercareRecords, createAftercareRecord, extractErrorMessage } from '../services/api';
import { FaPlus, FaArrowUp, FaArrowDown, FaMinus } from 'react-icons/fa';
import UserSelect from '../components/UserSelect';
import { useToast } from '../components/Toast';

function Aftercare({ user }) {
  const toast = useToast();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [newRecord, setNewRecord] = useState({
    user_id: '',
    session_id: '',
    type: 'standard',
    mood_before: 5,
    mood_after: 5,
    needs: '',
    notes: '',
    actions_taken: ''
  });

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const data = await getAftercareRecords();
      setRecords(data.records || data || []);
    } catch (error) {
      console.error('Error fetching aftercare records:', error);
      toast.error(extractErrorMessage(error, 'Aftercare-Einträge konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (saving) return; // prevent double-submit
    setSaving(true);
    setFormError(null);
    try {
      await createAftercareRecord(newRecord);
      // Only close & reset AFTER successful save
      setNewRecord({ user_id: '', session_id: '', type: 'standard', mood_before: 5, mood_after: 5, needs: '', notes: '', actions_taken: '' });
      setShowModal(false);
      toast.success('Aftercare-Eintrag gespeichert');
      fetchRecords();
    } catch (error) {
      console.error('Error creating aftercare record:', error);
      setFormError(extractErrorMessage(error, 'Fehler beim Erstellen des Aftercare-Eintrags'));
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setFormError(null);
  };

  const getMoodChange = (before, after) => {
    const diff = after - before;
    if (diff > 0) return { icon: <FaArrowUp />, className: 'mood-up', label: `+${diff}` };
    if (diff < 0) return { icon: <FaArrowDown />, className: 'mood-down', label: `${diff}` };
    return { icon: <FaMinus />, className: 'mood-same', label: '0' };
  };

  const getMoodColor = (value) => {
    if (value >= 8) return '#43b581';
    if (value >= 6) return '#7289da';
    if (value >= 4) return '#faa61a';
    return '#f04747';
  };

  const aftercareTypes = ['standard', 'intensive', 'light', 'remote', 'self-care'];

  const getTypeLabel = (type) => {
    const labels = { standard: 'Standard', intensive: 'Intensiv', light: 'Leicht', remote: 'Remote', 'self-care': 'Selbstfürsorge' };
    return labels[type] || type;
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1>Aftercare</h1>
          <button className="add-btn" onClick={() => setShowModal(true)}>
            <FaPlus /> Neuer Eintrag
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="aftercare-list">
            {records.length > 0 ? records.map(record => {
              const moodChange = getMoodChange(record.mood_before, record.mood_after);
              return (
                <div key={record.id} className="checkin-card">
                  <div className="checkin-header">
                    <span className="checkin-date">
                      {new Date(record.created_at || record.timestamp).toLocaleDateString('de-DE', {
                        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
                      })}
                    </span>
                    <span className="badge">{getTypeLabel(record.type)}</span>
                  </div>

                  <div className="mood-comparison">
                    <div className="mood-item">
                      <span className="mood-label">Stimmung vorher</span>
                      <div className="meter-bar">
                        <div className="meter-fill" style={{ width: `${(record.mood_before / 10) * 100}%`, background: getMoodColor(record.mood_before) }}></div>
                      </div>
                      <span className="meter-value">{record.mood_before}/10</span>
                    </div>
                    <div className="mood-change-indicator">
                      <span className={`mood-change ${moodChange.className}`}>
                        {moodChange.icon} {moodChange.label}
                      </span>
                    </div>
                    <div className="mood-item">
                      <span className="mood-label">Stimmung nachher</span>
                      <div className="meter-bar">
                        <div className="meter-fill" style={{ width: `${(record.mood_after / 10) * 100}%`, background: getMoodColor(record.mood_after) }}></div>
                      </div>
                      <span className="meter-value">{record.mood_after}/10</span>
                    </div>
                  </div>

                  {record.needs && (
                    <div className="detail-row">
                      <strong>Bedürfnisse:</strong> {record.needs}
                    </div>
                  )}
                  {record.actions_taken && (
                    <div className="detail-row">
                      <strong>Maßnahmen:</strong> {record.actions_taken}
                    </div>
                  )}
                  {record.notes && <p className="checkin-notes">{record.notes}</p>}
                </div>
              );
            }) : (
              <div className="no-data">Keine Aftercare-Einträge gefunden</div>
            )}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neuer Aftercare-Eintrag</h2>
              {formError && <div className="shared-form-error-banner">{formError}</div>}
              <form onSubmit={handleCreate}>
                <div className="form-group">
                  <UserSelect
                    value={newRecord.user_id}
                    onChange={id => setNewRecord({...newRecord, user_id: id})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Session ID (optional)</label>
                  <input
                    type="text"
                    value={newRecord.session_id}
                    onChange={e => setNewRecord({...newRecord, session_id: e.target.value})}
                    placeholder="Zugehörige Session-ID"
                  />
                </div>
                <div className="form-group">
                  <label>Typ</label>
                  <select
                    value={newRecord.type}
                    onChange={e => setNewRecord({...newRecord, type: e.target.value})}
                  >
                    {aftercareTypes.map(t => (
                      <option key={t} value={t}>{getTypeLabel(t)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Stimmung vorher: {newRecord.mood_before}/10</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={newRecord.mood_before}
                    onChange={e => setNewRecord({...newRecord, mood_before: parseInt(e.target.value)})}
                    className="slider"
                  />
                </div>
                <div className="form-group">
                  <label>Stimmung nachher: {newRecord.mood_after}/10</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={newRecord.mood_after}
                    onChange={e => setNewRecord({...newRecord, mood_after: parseInt(e.target.value)})}
                    className="slider"
                  />
                </div>
                <div className="form-group">
                  <label>Bedürfnisse</label>
                  <textarea
                    value={newRecord.needs}
                    onChange={e => setNewRecord({...newRecord, needs: e.target.value})}
                    placeholder="Was wird gebraucht?"
                  />
                </div>
                <div className="form-group">
                  <label>Maßnahmen</label>
                  <textarea
                    value={newRecord.actions_taken}
                    onChange={e => setNewRecord({...newRecord, actions_taken: e.target.value})}
                    placeholder="Was wurde unternommen?"
                  />
                </div>
                <div className="form-group">
                  <label>Notizen</label>
                  <textarea
                    value={newRecord.notes}
                    onChange={e => setNewRecord({...newRecord, notes: e.target.value})}
                    placeholder="Weitere Notizen..."
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeModal} disabled={saving}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={saving}>
                    {saving ? 'Speichert…' : 'Erstellen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Aftercare;
